import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import * as S from './Pagination.styles';

export interface PaginationProps {
	currentPage: number;
	pageCount: number;
	pageSize: number;
	sortField?: string;
	sortDirection?: string;
	sortFieldOptions?: { value: string; label: string }[];
	onPageChange: (newPage: number) => void;
	onPageSizeChange: (newSize: number) => void;
	onSortFieldChange?: (newField: string) => void;
	onSortDirectionChange?: (newDirection: string) => void;
	pageSizeOptions?: number[];
	showSort?: boolean;
}
const Pagination: FC<PaginationProps> = ({
	currentPage,
	pageCount,
	pageSize,
	sortField,
	sortDirection,
	sortFieldOptions = [],
	onPageChange,
	onPageSizeChange,
	onSortFieldChange,
	onSortDirectionChange,
	pageSizeOptions = [5, 10, 20, 50, 100],
	showSort = true,
}) => {
	// State to track if we should use compact labels
	const [useCompactLabels, setUseCompactLabels] = useState(false);
	const [startEllipsisOpen, setStartEllipsisOpen] = useState(false);
	const [endEllipsisOpen, setEndEllipsisOpen] = useState(false);
	const startEllipsisRef = useRef<HTMLDivElement>(null);
	const endEllipsisRef = useRef<HTMLDivElement>(null);

	// Effect to check window width and set compact mode
	useEffect(() => {
		const checkWidth = () => {
			setUseCompactLabels(window.innerWidth < 600);
		};

		// Initial check
		checkWidth();

		// Add resize listener
		window.addEventListener('resize', checkWidth);

		// Cleanup
		return () => window.removeEventListener('resize', checkWidth);
	}, []);

	// Calculate pages to show in ellipsis dropdowns
	const getIntermediatePages = (start: number, end: number) => {
		const totalPages = end - start - 1;

		// For smaller ranges (8 or fewer), just show all pages between start and end
		if (totalPages <= 8) {
			return Array.from({ length: totalPages }, (_, i) => start + i + 1);
		}

		// For larger ranges, we need evenly spaced buttons with consistent intervals
		// Ideal case: 8 buttons maximum
		const desiredButtons = 8;

		// Calculate how many intervals we need
		const intervals = desiredButtons - 1;

		// Force a minimum interval of 2 for ranges larger than 16 pages
		// This ensures we get numbers like [3,5,7,9,11,13] instead of [3,4,5,6,7,8,9,10]
		const interval =
			totalPages > 16
				? Math.max(2, Math.ceil(totalPages / intervals))
				: Math.ceil(totalPages / intervals);

		// Generate the evenly spaced page numbers
		const result = [];
		for (let pageNum = start + 1; pageNum < end; pageNum += interval) {
			result.push(pageNum);

			// Limit to 8 buttons total
			if (result.length >= desiredButtons) break;
		}

		// Always include the last page before end if not already present
		if (!result.includes(end - 1) && end - 1 > start + 1) {
			// If we already have 8 buttons, replace the last one
			if (result.length >= desiredButtons) {
				result[result.length - 1] = end - 1;
			} else {
				result.push(end - 1);
			}
		}

		return result;
	};

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				startEllipsisRef.current &&
				!startEllipsisRef.current.contains(event.target as Node)
			) {
				setStartEllipsisOpen(false);
			}
			if (
				endEllipsisRef.current &&
				!endEllipsisRef.current.contains(event.target as Node)
			) {
				setEndEllipsisOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const newSize = parseInt(e.target.value, 10);
		onPageSizeChange(newSize);
	};

	const handleSortFieldChange = (e: ChangeEvent<HTMLSelectElement>) => {
		if (onSortFieldChange) {
			onSortFieldChange(e.target.value);
		}
	};

	const handleSortDirectionChange = (e: ChangeEvent<HTMLSelectElement>) => {
		if (onSortDirectionChange) {
			onSortDirectionChange(e.target.value);
		}
	};

	return (
		<S.Controls>
			{/* Left side: Page size selector */}
			<S.SortControls style={{ flex: '0 0 auto' }}>
				{showSort && (
					<S.SortLabel>Ordenar:</S.SortLabel>
				)}
				{showSort &&
					sortField &&
					onSortFieldChange &&
					onSortDirectionChange && (
					<>
						<S.SelectControl
							onChange={handleSortFieldChange}
							value={sortField}
							aria-label="Camp d'ordenació"
						>
							{sortFieldOptions.map((option) => (
								<option
									key={option.value}
									value={option.value}
								>
									{option.label}
								</option>
							))}
						</S.SelectControl>
						<S.SelectControl
							onChange={handleSortDirectionChange}
							value={sortDirection}
							aria-label="Direcció d'ordenació"
						>
							<option value="asc">Ascendent</option>
							<option value="desc">Descendent</option>
						</S.SelectControl>
					</>
				)}
				<S.SelectControl
					onChange={handlePageSizeChange}
					value={pageSize}
					aria-label="Elements per pàgina"
				>
					{pageSizeOptions.map((size) => (
						<option key={size} value={size}>
							{size}
							{useCompactLabels ? '' : ' per pàgina'}
						</option>
					))}
				</S.SelectControl>
			</S.SortControls>

			{/* Right side: Page navigation buttons */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					flex: '1 1 auto',
					minWidth: 0,
					overflow: 'visible', // Allow dropdowns to be visible
				}}
			>
				<S.PaginationControls>
					{/* Previous Button */}
					<S.ArrowButtonControl
						onClick={() =>
							onPageChange(
								currentPage === 1 ? pageCount : currentPage - 1
							)
						}
						$isVisible={pageCount > 1}
					>
						&lt;
					</S.ArrowButtonControl>

					{/* Page Numbers */}
					{pageCount <= 6 ? (
						// Simple pagination for 6 or fewer pages
						Array.from({ length: pageCount }, (_, i) => i + 1).map(
							(page) => (
								<S.ButtonControl
									key={page}
									onClick={() =>
										page !== currentPage &&
										onPageChange(page)
									}
									$isCurrent={page === currentPage}
									disabled={page === currentPage}
									data-is-current={page === currentPage}
									$isVisible={true}
								>
									{page}
								</S.ButtonControl>
							)
						)
					) : (
						// Complex pagination for more than 6 pages
						<>
							{/* First Page */}
							<S.ButtonControl
								onClick={() => onPageChange(1)}
								$isVisible={currentPage !== 1}
							>
								1
							</S.ButtonControl>

							{/* First Ellipsis */}
							<S.EllipsisContainer ref={startEllipsisRef}>
								<S.ButtonControl
									$isVisible={currentPage > 3}
									$isEllipsis={true}
									$isActive={startEllipsisOpen}
									onClick={() => {
										console.log(
											'Opening start ellipsis dropdown'
										);
										setStartEllipsisOpen(
											!startEllipsisOpen
										);
									}}
								>
									...
								</S.ButtonControl>
								{startEllipsisOpen && currentPage > 3 && (
									<S.EllipsisDropdown>
										{getIntermediatePages(
											1,
											currentPage - 1
										).map((page) => (
											<S.DropdownItem
												key={page}
												onClick={(e) => {
													e.stopPropagation(); // Prevent event from reaching button
													onPageChange(page);
													setStartEllipsisOpen(false);
												}}
											>
												{page}
											</S.DropdownItem>
										))}
									</S.EllipsisDropdown>
								)}
							</S.EllipsisContainer>

							{/* Previous Page */}
							<S.ButtonControl
								onClick={() => onPageChange(currentPage - 1)}
								$isVisible={currentPage > 2}
							>
								{currentPage - 1}
							</S.ButtonControl>

							{/* Current Page */}
							<S.ButtonControl
								$isCurrent={true}
								$isVisible={true}
								disabled
								data-is-current={true}
							>
								{currentPage}
							</S.ButtonControl>

							{/* Next Page */}
							<S.ButtonControl
								onClick={() => onPageChange(currentPage + 1)}
								$isVisible={currentPage < pageCount - 1}
							>
								{currentPage + 1}
							</S.ButtonControl>

							{/* Last Ellipsis */}
							<S.EllipsisContainer ref={endEllipsisRef}>
								<S.ButtonControl
									$isVisible={currentPage < pageCount - 2}
									$isEllipsis={true}
									$isActive={endEllipsisOpen}
									onClick={() => {
										console.log(
											'Opening end ellipsis dropdown'
										);
										setEndEllipsisOpen(!endEllipsisOpen);
									}}
								>
									...
								</S.ButtonControl>
								{endEllipsisOpen &&
									currentPage < pageCount - 2 && (
									<S.EllipsisDropdown>
										{getIntermediatePages(
											currentPage + 1,
											pageCount
										).map((page) => (
											<S.DropdownItem
												key={page}
												onClick={(e) => {
													e.stopPropagation(); // Prevent event from reaching button
													onPageChange(page);
													setEndEllipsisOpen(
														false
													);
												}}
											>
												{page}
											</S.DropdownItem>
										))}
									</S.EllipsisDropdown>
								)}
							</S.EllipsisContainer>

							{/* Last Page */}
							<S.ButtonControl
								onClick={() => onPageChange(pageCount)}
								$isVisible={currentPage !== pageCount}
							>
								{pageCount}
							</S.ButtonControl>
						</>
					)}

					<S.ArrowButtonControl
						onClick={() =>
							onPageChange(
								currentPage === pageCount ? 1 : currentPage + 1
							)
						}
						$isVisible={pageCount > 1}
					>
						&gt;
					</S.ArrowButtonControl>
				</S.PaginationControls>
			</div>
		</S.Controls>
	);
};

export default Pagination;
